import { settings } from '@/config/settings';
import type { InputDevice, InputState } from '@/input/types';
import type { World } from '@/scene/world';

/**
 * Full assist mode: when auto-aim is enabled (setting on any device,
 * mandatory on touch) the ship aims at the boss AND fires by itself —
 * the player only flies and times dash/supershot.
 */
export function applyAutoAim(
  input: InputState, device: InputDevice, world: World, bossHeld: boolean,
): void {
  const wanted = settings.autoAim || device === 'touch';
  if (!wanted) return;
  const { boss, player } = world;
  if (!boss || boss.dead) return;
  // Player intent WINS (user request): a deflected right stick — or
  // mouse-aim while the trigger is held — locks aim to the player's
  // hand for as long as they hold it; assist resumes on release.
  const manual =
    (device === 'gamepad' && input.hasAim) ||
    (device === 'keyboard' && input.hasAim && input.fire);
  if (manual) {
    input.fire = true; // autofire stays on, direction is all theirs
    return;
  }
  // Smarter targeting (docs/12 #4 fix): aim at the NEAREST valid target, not
  // always the boss. A minion at point blank gets shot before an out-of-reach
  // boss; a shielded/held boss is never the target while minions live.
  // Multi-part bosses hand back the part to shoot (turret, arm tip — X7).
  const aim = boss.aimPoint(player.x, player.y);
  let target: { x: number; y: number } = aim;
  const m = world.minions.nearest(player.x, player.y);
  if (bossHeld || !boss.isVulnerable) {
    target = m ?? aim;
  } else if (m) {
    const mDist = Math.hypot(m.x - player.x, m.y - player.y);
    const bDist = Math.hypot(aim.x - player.x, aim.y - player.y);
    if (mDist < bDist) target = m;
  }
  const dx = target.x - player.x;
  const dy = target.y - player.y;
  const d = Math.hypot(dx, dy) || 1;
  input.aimX = dx / d;
  input.aimY = dy / d;
  input.hasAim = true;
  input.fire = true;
}

/**
 * Homing follows the actual kill path (docs/12 L3): nearest valid target —
 * and NEVER a shielded boss while minions hold the key.
 */
export function updateHoming(world: World, bossHeld: boolean): void {
  const { boss, player, bullets } = world;
  if (!boss || boss.dead) {
    bullets.clearHomingTarget();
    return;
  }
  const m = world.minions.nearest(player.x, player.y);
  const aim = boss.aimPoint(player.x, player.y);
  let hx = aim.x;
  let hy = aim.y;
  if (m) {
    const mDist = Math.hypot(m.x - player.x, m.y - player.y);
    const bDist = Math.hypot(aim.x - player.x, aim.y - player.y);
    if (bossHeld || !boss.isVulnerable || mDist < bDist) {
      hx = m.x;
      hy = m.y;
    }
  }
  bullets.setHomingTarget(hx, hy);
}
